// TB-076: pure helpers for the one web booking flow (no Vue, no http), so they can be unit tested.
import { formatFee } from '@/utils/money'
import { allowsCallJoin, visitTypeLabel as visitTypeLabelOf } from '@/utils/visitTypes'
import { wallToInstant } from '@/utils/locale'

// The web keeps an unpaid booking for this long before it releases it. The server also holds
// it (appointment status pending_payment, 15 min, TB-084), so the web hold is the one users see.
export const HOLD_MINUTES = 10
export const HOLD_MS = HOLD_MINUTES * 60 * 1000

// transactions-api `payment` charges the slot price plus the EFFECTIVE tax rate (TB-084):
// the doctor's override, else the establishment rate, else the admin global default.
// The review reads it from GET tax/effective; TAX_RATE (the seeded global default, 2%) is only
// the fallback when that call fails.
export const TAX_RATE = 0.02

// API percent (5, 7.5) -> fraction (0.05, 0.075); null when it is not a valid 0..100 number.
export function taxRateFromPercent (percent) {
  if (percent === null || percent === undefined || percent === '') return null
  const n = Number(percent)
  if (!Number.isFinite(n) || n < 0 || n > 100) return null
  return n / 100
}

// Fraction -> label text without float noise: 0.02 -> "2", 0.075 -> "7.5", 0.0525 -> "5.25".
export function formatTaxPercent (rate) {
  const n = Number(rate)
  if (!Number.isFinite(n)) return '0'
  return String(Math.round(n * 10000) / 100)
}

// The tax rate depends on the doctor and the place of service of the slot.
export function taxKey (slot) {
  if (!slot || !slot.providerId) return ''
  return `${slot.providerId}|${slot.posId || ''}`
}

export const STEPS = [
  { key: 'doctor', label: 'Doctor' },
  { key: 'time', label: 'Time' },
  { key: 'patient', label: 'Patient' },
  { key: 'review', label: 'Review' },
  { key: 'pay', label: 'Pay' },
  { key: 'done', label: 'Done' }
]

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100

export function slotPrice (slot) {
  if (!slot) return 0
  const raw = slot.perSlotAmount ?? (slot.schedule && slot.schedule.perSlotAmount)
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function priceBreakdown (amount, taxRate = TAX_RATE) {
  const rate = taxRate === null || taxRate === '' || !Number.isFinite(Number(taxRate)) ? TAX_RATE : Number(taxRate)
  const price = round2(Number(amount) || 0)
  const tax = round2(price * rate)
  return { price, tax, total: round2(price + tax), taxRate: rate }
}

// Fees are BHD as stored (TB-110).
export function formatMoney (n) {
  return formatFee(Number(n) || 0)
}

export function holdRemainingMs (heldAt, now = Date.now()) {
  if (!heldAt) return null
  const start = typeof heldAt === 'number' ? heldAt : Date.parse(heldAt)
  if (!Number.isFinite(start)) return null
  return Math.max(0, start + HOLD_MS - now)
}

export function isHoldExpired (heldAt, now = Date.now()) {
  const left = holdRemainingMs(heldAt, now)
  return left !== null && left <= 0
}

export function formatCountdown (ms) {
  if (ms === null || ms === undefined) return ''
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// TB-192: the place of the slot's purpose (schedule/slots returns it with its pictures).
export function slotLocation (slot) {
  const p = slot && slot.schedule && slot.schedule.purposes
  return (p && p.location) || null
}

export function slotType (slot) {
  if (!slot) return ''
  return slot.type || (slot.schedule && slot.schedule.consultationType && slot.schedule.consultationType.title) || ''
}

// "2026-10-20" + "09:30" (clinic wall time) -> the real instant (TB-123: via clinicTimeZone).
export function slotStart (slot) {
  if (!slot || !slot.consultationDate || !slot.startTime) return null
  const d = wallToInstant(String(slot.consultationDate).slice(0, 10), slot.startTime)
  return isNaN(d) ? null : d
}

export function slotEnd (slot) {
  if (!slot || !slot.consultationDate || !slot.endTime) return null
  const d = wallToInstant(String(slot.consultationDate).slice(0, 10), slot.endTime)
  return isNaN(d) ? null : d
}

export function appointmentPayload (slot, patientId) {
  return {
    slotId: slot.id,
    posId: slot.posId,
    providerId: slot.providerId,
    lovedOneId: patientId,
    consultationTypeId: slot.consultationTypeId
  }
}

export function paymentPayload ({ appointmentId, paymentType, card, amount }) {
  const base = { appointmentId, paymentType, totalAmount: Number(amount) || 0 }
  if (paymentType !== 'card') return base
  return { ...base, cardId: card && card.stripeCardId, customerId: card && card.stripeCustomerId }
}

// Calendar: UTC stamps so the event lands at the same instant in any calendar.
function icsStamp (d) {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function icsEscape (s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1')
}

export function calendarEvent (summary) {
  const start = summary && summary.start ? new Date(summary.start) : null
  if (!start || isNaN(start)) return null
  let end = summary.end ? new Date(summary.end) : null
  if (!end || isNaN(end) || end <= start) end = new Date(start.getTime() + (30 * 60 * 1000))
  const who = summary.doctorName ? `Appointment with ${summary.doctorName}` : 'Tabibbi appointment'
  const title = summary.type ? `${who} (${visitTypeLabelOf(summary.type)})` : who
  const details = [
    summary.patientName ? `Patient: ${summary.patientName}` : '',
    summary.type && allowsCallJoin(summary.type) ? 'Join from Tabibbi > Appointments.' : '',
    'Booked on Tabibbi.'
  ].filter(Boolean).join('\n')
  return { title, details, location: summary.location || '', start, end, uid: summary.appointmentId || String(start.getTime()) }
}

export function buildIcs (summary, now = new Date()) {
  const ev = calendarEvent(summary)
  if (!ev) return ''
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Tabibbi//Booking//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${icsEscape(ev.uid)}@tabibbi`,
    `DTSTAMP:${icsStamp(now)}`,
    `DTSTART:${icsStamp(ev.start)}`,
    `DTEND:${icsStamp(ev.end)}`,
    `SUMMARY:${icsEscape(ev.title)}`,
    `DESCRIPTION:${icsEscape(ev.details)}`,
    ev.location ? `LOCATION:${icsEscape(ev.location)}` : null,
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(Boolean).join('\r\n')
}

export function googleCalendarUrl (summary) {
  const ev = calendarEvent(summary)
  if (!ev) return ''
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: `${icsStamp(ev.start)}/${icsStamp(ev.end)}`,
    details: ev.details
  })
  if (ev.location) p.set('location', ev.location)
  return `https://calendar.google.com/calendar/render?${p.toString()}`
}

// Human message for a failed payment (Stripe / API text when there is one).
export function paymentErrorMessage (err) {
  const msg = err && err.response && err.response.data && err.response.data.message
  if (Array.isArray(msg)) return msg.join(' ')
  if (typeof msg === 'string' && msg.trim()) return msg
  if (err && !err.response && (err.request || err.isAxiosError || err.message === 'Network Error')) return 'We could not reach the payment service. Check your connection and try again.'
  if (err && !err.response && err.message) return err.message
  return 'Your payment did not go through.'
}

export function personName (p) {
  if (!p) return ''
  const n = [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
  return n || p.name || ''
}

// POST payment answers data { appointmentId, basicFee, taxRate (percent), tax, totalAmount }
// (TB-084). -> the confirmation price { price, tax, total, taxRate (fraction) }, or null when
// the answer does not carry the amounts (then the computed values are kept).
export function chargedFromPayment (data) {
  if (!data || typeof data !== 'object') return null
  const price = Number(data.basicFee)
  const tax = Number(data.tax)
  const total = Number(data.totalAmount)
  const amounts = [data.basicFee, data.tax, data.totalAmount]
  if (amounts.some((v) => v === null || v === undefined || v === '')) return null
  if (![price, tax, total].every(Number.isFinite)) return null
  const rate = taxRateFromPercent(data.taxRate)
  return {
    price: round2(price),
    tax: round2(tax),
    total: round2(total),
    taxRate: rate === null ? (price > 0 ? tax / price : 0) : rate
  }
}
